import React from 'react';
import { View, Text, StyleSheet, Platform, Image } from 'react-native';
import { formatUserName } from '../../../utils/emailTouserName';

// Import logo
const warehouseNavigatorLogo = require('../../../assets/warehouse_navigator.png');

interface Schedule {
  'Loc ID': string;
  'YR': number;
  'PRD': number;
  'WK': number;
  'is_fav': boolean;
  'fav_name': string | null;
  'total_forecasted_cases': number | null;
  'total_scheduled_hours': number;
  'total_actual_hours': number;
  'recommended_hours': number | null;
  'CreatedAt': string;
  'CreatedBy': string | null;
  'ModifiedAt': string | null;
  'ModifiedBy': string | null;
}

interface EmployeeRow {
  id: string;
  employee: { id: string; value: string; label: string } | null;
  role: { id: string; value: string; label: string } | null;
  shift: { id: string; value: string; label: string } | null;
  sunday: string;
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  total: string;
  isChildRow?: boolean;
}

interface DLOPrintScheduleProps {
  scheduleDetails: Schedule | null;
  employeeRows: EmployeeRow[];
  locationName?: string;
  periodWeekYear: string;
  activeTab: 'schedule' | 'actual';
  hideHours?: boolean;
}

const DLOPrintSchedule: React.FC<DLOPrintScheduleProps> = ({
  scheduleDetails,
  employeeRows,
  locationName = 'Unknown Location',
  periodWeekYear,
  activeTab,
  hideHours = false
}) => {
  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return dateString;
    }
  };

  // Helper function to format hour display based on hideHours flag
  // If hideHours is true: show 'X' for values > 0, show empty string for 0
  // If hideHours is false: show the actual value
  const formatHourDisplay = (value: string | number): string => {
    if (!hideHours) {
      return String(value || '0');
    }
    
    const numValue = parseFloat(String(value)) || 0;
    if (numValue > 0) {
      return 'X';
    }
    return '';
  };

  // Calculate totals for employee schedule - include all rows (parent and child)
  const calculateDayTotal = (day: 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday') => {
    return employeeRows
      .reduce((sum, row) => sum + (parseFloat(row[day]) || 0), 0)
      .toFixed(2);
  };

  const calculateGrandTotal = () => {
    return employeeRows
      .reduce((sum, row) => sum + (parseFloat(row.total) || 0), 0)
      .toFixed(2);
  };

  // Include all rows (parent and child) for display
  const displayRows = employeeRows;

  return (
    <View style={styles.printContainer}>
      {/* Header with Logo */}
      <View style={styles.headerSection}>
        <Image 
          source={warehouseNavigatorLogo} 
          style={styles.headerLogo}
          resizeMode="contain"
        />
        <Text style={styles.mainTitle}>Labor Optimization Schedule Report</Text>
      </View>

      {/* Schedule Details Section - Page 1 */}
      <View style={[styles.section, styles.pageBreakAfter]}>
        <Text style={styles.sectionTitle}>Schedule Details</Text>
        <View style={styles.detailsTable}>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Location</Text>
            <Text style={styles.detailValue}>{locationName}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Period/Week</Text>
            <Text style={styles.detailValue}>{periodWeekYear}</Text>
          </View>
          {scheduleDetails && (
            <>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Created By</Text>
                <Text style={styles.detailValue}>{formatUserName((scheduleDetails.CreatedBy) || 'N/A')}</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Modified By</Text>
                <Text style={styles.detailValue}>{formatUserName((scheduleDetails.ModifiedBy) || 'N/A')}</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Forecasted Cases</Text>
                <Text style={styles.detailValue}>
                  {scheduleDetails.total_forecasted_cases?.toLocaleString() || '0'}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Actual Cases</Text>
                <Text style={styles.detailValue}>0</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Recommended Hours</Text>
                <Text style={styles.detailValue}>
                  {scheduleDetails.recommended_hours?.toFixed(1) || '0.0'}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Scheduled Hours</Text>
                <Text style={styles.detailValue}>
                  {scheduleDetails.total_scheduled_hours?.toFixed(1) || '0.0'}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Estimated Hours</Text>
                <Text style={styles.detailValue}>
                  {(scheduleDetails.total_scheduled_hours * 1.618)?.toFixed(1) || '0.0'}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Actual Hours</Text>
                <Text style={styles.detailValue}>
                  {scheduleDetails.total_actual_hours?.toFixed(1) || '0.0'}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Forecasted KCPH</Text>
                <Text style={styles.detailValue}>
                  {scheduleDetails.total_forecasted_cases && scheduleDetails.recommended_hours
                    ? (scheduleDetails.total_forecasted_cases / scheduleDetails.recommended_hours).toFixed(1)
                    : '0.0'}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Target KCPH</Text>
                <Text style={styles.detailValue}>
                  {scheduleDetails.total_forecasted_cases && scheduleDetails.recommended_hours
                    ? ((scheduleDetails.total_forecasted_cases / scheduleDetails.recommended_hours) * 0.467).toFixed(1)
                    : '0.0'}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Scheduled KCPH</Text>
                <Text style={styles.detailValue}>
                  {scheduleDetails.total_forecasted_cases && scheduleDetails.total_scheduled_hours
                    ? (scheduleDetails.total_forecasted_cases / scheduleDetails.total_scheduled_hours).toFixed(1)
                    : '0.0'}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Estimated KCPH</Text>
                <Text style={styles.detailValue}>
                  {scheduleDetails.total_forecasted_cases && scheduleDetails.total_scheduled_hours
                    ? (scheduleDetails.total_forecasted_cases / (scheduleDetails.total_scheduled_hours * 1.618)).toFixed(1)
                    : '0.0'}
                </Text>
              </View>
            </>
          )}
        </View>
      </View>

      {/* Employee Schedule Section - Page 2 */}
      <View style={[styles.section, styles.pageBreakBefore]}>
        <Text style={styles.sectionTitle}>
          Employee Schedule - {activeTab === 'schedule' ? 'Scheduled Hours' : 'Actual Hours'}
        </Text>
        <Text style={styles.periodInfo}>{periodWeekYear}</Text>
        
        <View style={styles.tableContainer}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.employeeColumn]}>Employee</Text>
            <Text style={[styles.tableHeaderCell, styles.roleColumn]}>Role</Text>
            <Text style={[styles.tableHeaderCell, styles.shiftColumn]}>Shift</Text>
            <Text style={[styles.tableHeaderCell, styles.dayColumn]}>Sun</Text>
            <Text style={[styles.tableHeaderCell, styles.dayColumn]}>Mon</Text>
            <Text style={[styles.tableHeaderCell, styles.dayColumn]}>Tue</Text>
            <Text style={[styles.tableHeaderCell, styles.dayColumn]}>Wed</Text>
            <Text style={[styles.tableHeaderCell, styles.dayColumn]}>Thu</Text>
            <Text style={[styles.tableHeaderCell, styles.dayColumn]}>Fri</Text>
            <Text style={[styles.tableHeaderCell, styles.dayColumn]}>Sat</Text>
            <Text style={[styles.tableHeaderCell, styles.totalColumn]}>Total</Text>
          </View>

          {/* Table Rows */}
          {displayRows.map((row, index) => (
            <View key={row.id} style={[
              styles.tableRow, 
              index % 2 === 1 && styles.tableRowEven,
              row.isChildRow && styles.childRow
            ]}>
              <Text style={[
                styles.tableCell, 
                styles.employeeColumn,
                row.isChildRow && styles.childRowText
              ]}>
                {row.isChildRow ? `↳ ${row.employee?.label || '-'}` : (row.employee?.label || '-')}
              </Text>
              <Text style={[styles.tableCell, styles.roleColumn]}>
                {row.role?.label || '-'}
              </Text>
              <Text style={[styles.tableCell, styles.shiftColumn]}>
                {row.shift?.label || '-'}
              </Text>
              <Text style={[styles.tableCell, styles.dayColumn]}>
                {formatHourDisplay(row.sunday)}
              </Text>
              <Text style={[styles.tableCell, styles.dayColumn]}>
                {formatHourDisplay(row.monday)}
              </Text>
              <Text style={[styles.tableCell, styles.dayColumn]}>
                {formatHourDisplay(row.tuesday)}
              </Text>
              <Text style={[styles.tableCell, styles.dayColumn]}>
                {formatHourDisplay(row.wednesday)}
              </Text>
              <Text style={[styles.tableCell, styles.dayColumn]}>
                {formatHourDisplay(row.thursday)}
              </Text>
              <Text style={[styles.tableCell, styles.dayColumn]}>
                {formatHourDisplay(row.friday)}
              </Text>
              <Text style={[styles.tableCell, styles.dayColumn]}>
                {formatHourDisplay(row.saturday)}
              </Text>
              <Text style={[styles.tableCell, styles.totalColumn, styles.totalCell]}>
                {formatHourDisplay(row.total)}
              </Text>
            </View>
          ))}

          {/* Total Row */}
          <View style={[styles.tableRow, styles.totalRow]}>
            <Text style={[styles.tableCell, styles.employeeColumn, styles.totalLabel]}>Total</Text>
            <Text style={[styles.tableCell, styles.roleColumn]}></Text>
            <Text style={[styles.tableCell, styles.shiftColumn]}></Text>
            <Text style={[styles.tableCell, styles.dayColumn, styles.totalCell]}>
              {formatHourDisplay(calculateDayTotal('sunday'))}
            </Text>
            <Text style={[styles.tableCell, styles.dayColumn, styles.totalCell]}>
              {formatHourDisplay(calculateDayTotal('monday'))}
            </Text>
            <Text style={[styles.tableCell, styles.dayColumn, styles.totalCell]}>
              {formatHourDisplay(calculateDayTotal('tuesday'))}
            </Text>
            <Text style={[styles.tableCell, styles.dayColumn, styles.totalCell]}>
              {formatHourDisplay(calculateDayTotal('wednesday'))}
            </Text>
            <Text style={[styles.tableCell, styles.dayColumn, styles.totalCell]}>
              {formatHourDisplay(calculateDayTotal('thursday'))}
            </Text>
            <Text style={[styles.tableCell, styles.dayColumn, styles.totalCell]}>
              {formatHourDisplay(calculateDayTotal('friday'))}
            </Text>
            <Text style={[styles.tableCell, styles.dayColumn, styles.totalCell]}>
              {formatHourDisplay(calculateDayTotal('saturday'))}
            </Text>
            <Text style={[styles.tableCell, styles.totalColumn, styles.totalCell]}>
              {formatHourDisplay(calculateGrandTotal())}
            </Text>
          </View>
        </View>
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          Printed on: {new Date().toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          })}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  printContainer: {
    backgroundColor: '#fff',
    padding: 20,
  },
  headerSection: {
    backgroundColor: '#003B73',
    padding: 20,
    marginBottom: 20,
    borderRadius: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: 100,
  },
  headerLogo: {
    width: 80,
    height: 80,
    marginRight: 20,
  },
  mainTitle: {
    fontSize: 32,
    fontWeight: '700',
    color: '#fff',
    textAlign: 'left',
    flex: 1,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  section: {
    marginBottom: 30,
  },
  pageBreakAfter: Platform.select({
    web: {
      // @ts-ignore - pageBreakAfter is a valid CSS property for web
      pageBreakAfter: 'always',
    },
    default: {},
  }) as any,
  pageBreakBefore: Platform.select({
    web: {
      // @ts-ignore - pageBreakBefore is a valid CSS property for web
      pageBreakBefore: 'always',
      marginTop: 0,
    },
    default: {},
  }) as any,
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#000',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 2,
    borderBottomColor: '#005CBC',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  periodInfo: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  detailsTable: {
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 4,
  },
  detailRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  detailLabel: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-SemiBold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  detailValue: {
    flex: 1,
    fontSize: 14,
    color: '#666',
    textAlign: 'right',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  tableContainer: {
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 4,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#005CBC',
    paddingVertical: 12,
  },
  tableHeaderCell: {
    fontSize: 12,
    fontWeight: '700',
    color: '#fff',
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  tableRowEven: {
    backgroundColor: '#F9F9F9',
  },
  childRow: {
    backgroundColor: '#F0F5FA',
  },
  tableCell: {
    fontSize: 11,
    color: '#333',
    textAlign: 'center',
    paddingHorizontal: 4,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  childRowText: {
    fontStyle: 'italic',
    color: '#555',
  },
  employeeColumn: {
    flex: 2,
    paddingHorizontal: 6,
    textAlign: 'left',
  },
  roleColumn: {
    flex: 1.5,
    paddingHorizontal: 4,
  },
  shiftColumn: {
    flex: 1.5,
    paddingHorizontal: 4,
  },
  dayColumn: {
    flex: 0.8,
    paddingHorizontal: 2,
  },
  totalColumn: {
    flex: 1,
    paddingHorizontal: 4,
  },
  totalRow: {
    backgroundColor: '#F0F0F0',
    borderBottomWidth: 0,
  },
  totalLabel: {
    fontWeight: '700',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  totalCell: {
    fontWeight: '700',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  footer: {
    marginTop: 30,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
  },
  footerText: {
    fontSize: 12,
    color: '#666',
    textAlign: 'right',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
});

export default DLOPrintSchedule;
